import { Link, usePathname, type Href } from 'expo-router';
import type { ReactNode } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { RivieraLogo } from '@/components/riviera-logo';
import { FooterColumn } from '@/components/riviera/footer-column';
import type { FooterLink } from '@/components/riviera/footer-column.types';
import { navHref } from '@/components/riviera/nav-links';
import { Fonts, MaxContentWidth, Spacing } from '@/constants/theme';
import { useTheme } from '@/hooks/use-theme';
import { cities } from '@/lib/events-data';
import { cityEventsCopy, cityToursCopy } from '@/lib/i18n/city-listings-content';
import { useLocaleLinks } from '@todo/core/i18n/locale-links-context';
import { LOCALE_NAMES, SUPPORTED_LOCALES } from '@todo/core/i18n/locales';
import { useLocale, withLocale } from '@todo/core/i18n/use-locale';
import { SITE_NAME } from '@todo/core/seo';

type Props = {
  /** City ids with an exported /boat-rental/[city] page. */
  boatRentalCityIds: string[];
  /** City ids with an exported /wine-tours/[city] page. */
  wineTourCityIds: string[];
  /** City ids with an exported /[city]/events page. */
  eventCityIds: string[];
  /** City ids with an exported /[city]/tours page. */
  tourCityIds: string[];
  /** Web only: the footer's baked seed <script>. */
  children?: ReactNode;
};

// The sister sites, linked from the footer's bottom row under their brand
// names. A locale one doesn't have (FR) links to its /en.
const SISTER_SITES = [
  { name: 'Dubai To-Do', url: 'https://dubai-todo.com' as const, locales: ['en', 'de', 'ru'] },
  { name: 'Miami To-Do', url: 'https://miami-todo.com' as const, locales: ['en', 'es', 'ru'] },
];

/**
 * Site-wide footer, rendered at the end of every page's scroll content.
 *
 * Its job is mostly SEO: it is the only block on every page that links to the
 * city landing pages (things to do, events, tours, boat rental, wine tours).
 * Without it those are reachable only through their hubs, so their
 * internal-link count, and how often they get crawled, is a fraction of what
 * the header's hubs get. Each
 * anchor is the page's own localized title ("Boat Rental in Cannes") — the
 * phrase that page is meant to rank for.
 *
 * Every entry is a real expo-router <Link> (an `<a href>` in the static HTML).
 * All but "things to do" list only the cities whose page was exported — see
 * site-footer.web.tsx.
 *
 * There's deliberately no column repeating the header nav (hubs like /events,
 * /articles): the header already renders those links into every page's static
 * HTML, at every width (CSS only hides them behind the hamburger on mobile), so
 * a footer copy added nothing but length.
 */
export function SiteFooterView({
  boatRentalCityIds,
  wineTourCityIds,
  eventCityIds,
  tourCityIds,
  children,
}: Props) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const pathname = usePathname();
  const localeLinks = useLocaleLinks();

  const thingsToDo: FooterLink[] = cities.map((city) => ({
    key: city.id,
    href: { pathname: '/[lang]/[city]', params: { lang: locale, city: city.id } },
    label: t(`cityPages.${city.id}.title`),
  }));

  const events: FooterLink[] = eventCityIds.map((id) => ({
    key: id,
    href: { pathname: '/[lang]/[city]/events', params: { lang: locale, city: id } },
    label: cityEventsCopy(locale, id)?.title ?? id,
  }));

  const tours: FooterLink[] = tourCityIds.map((id) => ({
    key: id,
    href: { pathname: '/[lang]/[city]/tours', params: { lang: locale, city: id } },
    label: cityToursCopy(locale, id)?.title ?? id,
  }));

  const boatRental: FooterLink[] = boatRentalCityIds.map((id) => ({
    key: id,
    href: { pathname: '/[lang]/boat-rental/[city]', params: { lang: locale, city: id } },
    label: t(`boatRental.cities.${id}.title`),
  }));

  const wineTours: FooterLink[] = wineTourCityIds.map((id) => ({
    key: id,
    href: { pathname: '/[lang]/wine-tours/[city]', params: { lang: locale, city: id } },
    label: t(`wineTours.cities.${id}.title`),
  }));

  const linkColor = { color: theme.mutedForeground };

  return (
    <View
      role="contentinfo"
      style={[styles.footer, { backgroundColor: theme.card, borderTopColor: theme.border }]}>
      <View style={styles.inner}>
        <View style={styles.columns}>
          <View style={styles.brandCol}>
            {/* Layout on the inner View — see the same Link in site-header.web.tsx. */}
            <Link href={navHref('/[lang]', locale)} asChild>
              <Pressable accessibilityRole="link" accessibilityLabel={t('a11y.home')}>
                <View style={styles.brandRow}>
                  <RivieraLogo size={32} color={theme.gold} />
                  <Text style={[styles.brandName, { color: theme.primary, fontFamily: Fonts?.serif }]}>
                    {SITE_NAME}
                  </Text>
                </View>
              </Pressable>
            </Link>
            <Text style={[styles.tagline, linkColor]}>{t('footer.tagline')}</Text>
          </View>

          <View style={styles.linkColumns}>
            <FooterColumn title={t('footer.thingsToDo')} links={thingsToDo} />
            <FooterColumn title={t('footer.events')} links={events} />
            <FooterColumn title={t('footer.tours')} links={tours} />
            <FooterColumn title={t('footer.boatRental')} links={boatRental} />
            <FooterColumn title={t('footer.wineTours')} links={wineTours} />
          </View>
        </View>

        <View style={[styles.bottom, { borderTopColor: theme.border }]}>
          <View style={styles.langRow}>
            <Text style={[styles.small, linkColor]}>{t('footer.language')}:</Text>
            {SUPPORTED_LOCALES.map((l) =>
              l === locale ? (
                <Text key={l} style={[styles.small, styles.currentLang, { color: theme.text }]}>
                  {LOCALE_NAMES[l]}
                </Text>
              ) : (
                // This page in the other language: the same destinations the
                // header's switcher navigates to, as crawlable links.
                <Link
                  key={l}
                  href={localeLinks?.[l] ?? (withLocale(pathname, l) as Href)}
                  style={[styles.small, linkColor]}>
                  {LOCALE_NAMES[l]}
                </Link>
              ),
            )}
          </View>
          <Text style={[styles.small, linkColor]}>{t('footer.disclosure')}</Text>
          <Text style={[styles.small, linkColor]}>
            {t('footer.sisterSites')}
            {SISTER_SITES.map((sister, i) => (
              <Text key={sister.url}>
                {i > 0 ? ' · ' : ' '}
                <Link
                  href={`${sister.url}/${sister.locales.includes(locale) ? locale : 'en'}`}
                  style={styles.sisterLink}>
                  {sister.name}
                </Link>
              </Text>
            ))}
          </Text>
          <Text style={[styles.small, linkColor]}>© {SITE_NAME}</Text>
        </View>
      </View>
      {children}
    </View>
  );
}

const styles = StyleSheet.create({
  footer: {
    width: '100%',
    alignItems: 'center',
    marginTop: Spacing.six,
    borderTopWidth: StyleSheet.hairlineWidth,
  },
  inner: {
    width: '100%',
    maxWidth: MaxContentWidth,
    paddingHorizontal: Spacing.three,
    paddingTop: Spacing.five,
    paddingBottom: Spacing.four,
  },
  // Brand block + one group of link columns. Desktop: side by side. Tablet:
  // the group wraps below the brand as a whole, keeping its five columns on
  // one row (instead of a lone column wrapping onto a row of its own). Phone:
  // each link column takes a full row as an accordion (see
  // footer-column.module.css). Columns pad their own bottom, so no rowGap —
  // the phone accordion rows must sit flush against each other.
  columns: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: Spacing.five,
  },
  linkColumns: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: Spacing.four,
    flexGrow: 3,
    // RN defaults flexShrink to 0: on a phone the group must shrink to the
    // screen instead of overflowing it at its basis. The basis is five 120px
    // columns plus their gaps, so the group only sits beside the brand when
    // all five fit on one row.
    flexShrink: 1,
    flexBasis: 5 * 120 + 4 * Spacing.four,
    minWidth: 0,
  },
  brandCol: {
    flexGrow: 1,
    flexBasis: 240,
    maxWidth: 360,
    gap: Spacing.two,
    paddingBottom: Spacing.four,
  },
  brandRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.two,
  },
  brandName: {
    fontSize: 18,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  tagline: {
    fontSize: 14,
    lineHeight: 21,
  },
  bottom: {
    paddingTop: Spacing.three,
    borderTopWidth: StyleSheet.hairlineWidth,
    gap: Spacing.two,
  },
  langRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: Spacing.two,
  },
  currentLang: {
    fontWeight: '600',
  },
  sisterLink: {
    textDecorationLine: 'underline',
  },
  small: {
    fontSize: 12,
    lineHeight: 18,
  },
});
